import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { ClipboardList, PackageOpen, CalendarX2, AlertTriangle } from 'lucide-react';
import { dashboardApi } from '@/lib/api';
import type { PharmacistDashboardData } from './types';
import {
  MetricCard, MetricsGrid, SectionCard, GenericBadge,
  DashboardShell, ScrollTable, Th, Td, LoadingState, ErrorState,
} from './_shared';

const RX_STATUS_MAP: Record<string, { label: string; variant: 'warning' | 'info' | 'success' | 'neutral' }> = {
  pending:    { label: 'Pending',    variant: 'warning' },
  processing: { label: 'Processing', variant: 'info'    },
  ready:      { label: 'Ready',      variant: 'success' },
  dispensed:  { label: 'Dispensed',  variant: 'neutral' },
};

// ── Component ───────────────────────────────────────────────────────────────
export default function PharmacistDashboard() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery<PharmacistDashboardData>({
    queryKey:        ['dashboard-pharmacy'],
    queryFn:         () => dashboardApi.pharmacy().then(r => r.data),
    staleTime:       60_000,
    refetchInterval: 2 * 60_000,
  });

  if (isLoading) return <LoadingState/>;
  if (isError || !data) return <ErrorState onRetry={() => refetch()}/>;

  return (
    <DashboardShell
      title="Pharmacy Dashboard"
      subtitle="Prescription queue · stock alerts · expiry watch"
      onRefresh={() => refetch()}
      refreshing={isFetching}>

      <MetricsGrid>
        <MetricCard icon={ClipboardList} label="Active Prescriptions"  value={data.activePrescriptions} accent="bg-orange/15"/>
        <MetricCard icon={PackageOpen}   label="Low Stock Warnings"    value={data.lowStockWarnings}    accent="bg-red-50"   alert={data.lowStockWarnings > 0}/>
        <MetricCard icon={CalendarX2}    label="Near-Expiry Batches"   value={data.nearExpiryBatches}   accent="bg-amber-50" alert={data.nearExpiryBatches > 0}/>
        <MetricCard icon={AlertTriangle} label="Actions Required"
          value={data.lowStockWarnings + data.nearExpiryBatches}
          accent="bg-red-50" alert={(data.lowStockWarnings + data.nearExpiryBatches) > 0}
        />
      </MetricsGrid>

      {/* Prescription Feed */}
      <SectionCard title="Prescription Feed">
        <ScrollTable>
          <thead>
            <tr>
              <Th>Rx ID</Th><Th>Patient</Th><Th>Doctor</Th>
              <Th>Issued</Th><Th right>Items</Th><Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {data.feed.map(rx => (
              <tr key={rx.rxId} className="hover:bg-gray-50 transition">
                <Td><span className="font-mono text-xs text-navy">{rx.rxId}</span></Td>
                <Td><span className="font-medium">{rx.patientName}</span></Td>
                <Td muted>{rx.doctorName}</Td>
                <Td muted>{rx.issuedAt}</Td>
                <Td right>{rx.itemCount}</Td>
                <Td>
                  <GenericBadge
                    label={RX_STATUS_MAP[rx.status]?.label ?? rx.status}
                    variant={RX_STATUS_MAP[rx.status]?.variant ?? 'neutral'}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </ScrollTable>
      </SectionCard>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Low Stock */}
        <SectionCard title="Low Stock Alerts">
          <ScrollTable>
            <thead>
              <tr>
                <Th>Drug</Th><Th right>Current</Th><Th right>Reorder At</Th><Th>Unit</Th>
              </tr>
            </thead>
            <tbody>
              {data.lowStock.map(s => (
                <tr key={s.drugName} className="hover:bg-gray-50 transition">
                  <Td><span className="font-medium">{s.drugName}</span></Td>
                  <Td right>
                    <span className={clsx('font-bold', s.currentQty <= s.reorderAt / 2 ? 'text-red-600' : 'text-amber-600')}>
                      {s.currentQty}
                    </span>
                  </Td>
                  <Td right muted>{s.reorderAt}</Td>
                  <Td muted>{s.unit}</Td>
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        </SectionCard>

        {/* Near Expiry */}
        <SectionCard title="Near-Expiry Batches">
          <ScrollTable>
            <thead>
              <tr>
                <Th>Drug</Th><Th>Batch</Th><Th right>Qty</Th><Th>Expires In</Th>
              </tr>
            </thead>
            <tbody>
              {data.expiry.map(e => (
                <tr key={e.batchNumber} className="hover:bg-gray-50 transition">
                  <Td><span className="font-medium">{e.drugName}</span></Td>
                  <Td muted><span className="font-mono text-xs">{e.batchNumber}</span></Td>
                  <Td right>{e.quantity}</Td>
                  <Td>
                    <span className={clsx(
                      'text-xs font-semibold',
                      e.daysToExpiry <= 14 ? 'text-red-600' : e.daysToExpiry <= 30 ? 'text-amber-600' : 'text-gray-500',
                    )}>
                      {e.daysToExpiry}d
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </ScrollTable>
        </SectionCard>
      </div>

    </DashboardShell>
  );
}
